import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing'

export interface CookieConsent {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

export interface CookieConsentContextType {
  consent: CookieConsent
  hasUserMadeChoice: boolean
  acceptAll: () => void
  acceptNecessary: () => void
  updateConsent: (consent: CookieConsent) => void
  showSettings: boolean
  setShowSettings: (show: boolean) => void
  resetConsent: () => void
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

const CONSENT_KEY = 'cookie_consent'
const CONSENT_VERSION = '1.0'

const defaultConsent: CookieConsent = {
  necessary: true, // Always true, cannot be disabled
  functional: false,
  analytics: false,
  marketing: false
}

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent)
  const [hasUserMadeChoice, setHasUserMadeChoice] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    // Load consent from localStorage on mount
    const savedConsent = localStorage.getItem(CONSENT_KEY)
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent)
        if (parsed.version === CONSENT_VERSION) {
          setConsent({ ...defaultConsent, ...parsed.consent })
          setHasUserMadeChoice(true)
        }
      } catch (error) {
        console.error('Failed to parse cookie consent:', error)
      }
    }
  }, [])

  const saveConsent = (newConsent: CookieConsent) => {
    const consentData = {
      consent: newConsent,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION
    }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData))
    setConsent(newConsent)
    setHasUserMadeChoice(true)
    
    // Clean up cookies based on consent
    cleanupCookies(newConsent)
  }

  const cleanupCookies = (newConsent: CookieConsent) => {
    // Remove analytics cookies if not consented
    if (!newConsent.analytics) {
      // Clear analytics-related localStorage/sessionStorage
      Object.keys(localStorage).forEach(key => {
        if (key.includes('analytics') || key.includes('ga_') || key.includes('_ga')) {
          localStorage.removeItem(key)
        }
      })
    }
    
    // Remove marketing cookies if not consented
    if (!newConsent.marketing) {
      Object.keys(localStorage).forEach(key => {
        if (key.includes('marketing') || key.includes('_fbp') || key.includes('_fbc')) {
          localStorage.removeItem(key)
        }
      })
    }
  }

  const acceptAll = () => {
    const allConsent: CookieConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    }
    saveConsent(allConsent)
  }

  const acceptNecessary = () => {
    saveConsent(defaultConsent)
  }

  const updateConsent = (newConsent: CookieConsent) => {
    saveConsent({ ...newConsent, necessary: true })
    setShowSettings(false)
  }

  const resetConsent = () => {
    localStorage.removeItem(CONSENT_KEY)
    setConsent(defaultConsent)
    setHasUserMadeChoice(false)
    cleanupCookies(defaultConsent)
  }

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasUserMadeChoice,
        acceptAll,
        acceptNecessary,
        updateConsent,
        showSettings,
        setShowSettings,
        resetConsent
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}